import Service, { service } from "@ember/service";
import { cached, tracked } from "@glimmer/tracking";
import { trackedArray } from "@ember/reactive/collections";
import { getOwnConfig, macroCondition } from "@embroider/macros";
import type StorageService from "glesys-controlpanel/services/storage";

interface Panel {
  title: string;
  global: boolean;
}

export interface DevToolsService {
  load<T>(key: string): T | null;
  save(key: string, data: unknown): void;
}

let DevToolsService: typeof Service;

if (macroCondition(getOwnConfig<{ devtools: boolean }>().devtools)) {
  class DevTools extends Service implements DevToolsService {
    @service declare storage: StorageService;

    @tracked target: HTMLElement | null = null;
    panels: Panel[] = trackedArray([]);

    open: boolean;
    @tracked activeTitle: string | null = null;

    #config: Record<string, unknown>;

    constructor(...args: ConstructorParameters<typeof Service>) {
      super(...args);
      this.#config = this.#parseConfig();
      const toolbar = this.load<{ open?: boolean; activeTab?: string | null }>("toolbar");
      this.open = Boolean(toolbar?.open);
      this.activeTitle = toolbar?.activeTab ?? null;
    }

    @cached
    get tabs(): Panel[] {
      return this.panels.toSorted((a, b) => Number(b.global) - Number(a.global));
    }

    get activeTab(): string | null {
      if (this.activeTitle && this.tabs.some((panel) => panel.title === this.activeTitle)) {
        return this.activeTitle;
      }
      return this.tabs[0]?.title ?? null;
    }

    registerPanel(title: string, global = false): void {
      if (!this.panels.some((panel) => panel.title === title)) {
        this.panels.push({ title, global });
      }
    }

    unregisterPanel(title: string): void {
      const index = this.panels.findIndex((panel) => panel.title === title);
      if (index !== -1) {
        this.panels.splice(index, 1);
      }
    }

    setActive = (title: string): void => {
      this.activeTitle = title;
      this.#persistToolbar();
    };

    setOpen(open: boolean): void {
      if (this.open === open) {
        return;
      }
      this.open = open;
      this.#persistToolbar();
    }

    load<T>(key: string): T | null {
      return (this.#config[key] as T) ?? null;
    }

    save(key: string, data: unknown): void {
      this.#config[key] = data;
      this.storage.set("glesys:devtools", JSON.stringify(this.#config));
    }

    #persistToolbar(): void {
      this.save("toolbar", { open: this.open, activeTab: this.activeTitle });
    }

    #parseConfig(): Record<string, unknown> {
      try {
        return (JSON.parse(this.storage.get("glesys:devtools") ?? "{}") as Record<string, unknown>) ?? {};
      } catch {
        return {};
      }
    }
  }

  DevToolsService = DevTools;
} else {
  DevToolsService = class extends Service {};
}

export default DevToolsService;
